// ─── utils/fonts.js ───────────────────────────────────────────────────────────
// Parses @font-face declarations and resolves computed fontFamily strings
// to cs_font_xxx identifiers.

/**
 * Scans all <style> elements in `doc` for @font-face blocks.
 *
 * @returns {{
*   fontFamilyMap: Record<string, string>,  // 'Gotham-Medium' → 'cs_font_66e7d...'
*   fontIdMap:     Record<string, string>,  // 'Gotham-Medium' → '66e7d...'
* }}
*/
export function extractFontFaceMap(doc) {
   const fontFamilyMap = {};
   const fontIdMap     = {};

   Array.from(doc.querySelectorAll('style')).forEach((styleEl) => {
       const blocks = styleEl.textContent.match(/@font-face\s*\{[^}]*\}/g) || [];
       blocks.forEach((block) => {
           const fm = block.match(/font-family\s*:\s*['"]?([^'";]+)['"]?\s*;/);
           const lm = block.match(/src\s*:\s*local\(['"]?([^'")\s]+)['"]?\)/);
           if (fm && lm) {
               const name      = fm[1].trim();
               const localName = lm[1].trim(); // e.g. cs_font_66e7d...
               fontFamilyMap[name] = localName;
               fontIdMap[name]     = localName.replace(/^cs_font_/, '');
           }
       });
   });

   return { fontFamilyMap, fontIdMap };
}

// CSS generic family keywords — excluded when looking for meaningful fallback names
const GENERIC_FAMILIES = new Set([
   'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui',
   'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', 'emoji',
   'math', 'fangsong', 'inherit', 'initial', 'unset', 'revert',
]);

/**
* Resolves a computed font-family stack to the first matching cs_font_xxx entry.
* Returns exclusively that ID — no generic fallback keywords are appended.
* If no cs_font match exists, returns the first non-generic named family.
*
* @returns {{ resolvedFamily: string|null, fontIds: string[] }}
*/
export function resolveFontFamily(computedFF, fontFamilyMap, fontIdMap) {
   if (!computedFF) return { resolvedFamily: null, fontIds: [] };

   const families     = computedFF
       .split(',')
       .map((f) => f.replace(/^["'\s]+|["'\s]+$/g, '').trim())
       .filter(Boolean);

   const fontIds      = [];
   let resolvedFamily = null;

   for (const family of families) {
       if (fontFamilyMap[family]) {
           resolvedFamily = fontFamilyMap[family]; // cs_font_xxx
           if (fontIdMap[family]) fontIds.push(fontIdMap[family]);
           break; // use the first cs_font hit exclusively
       }
       // Keep first non-generic named family as fallback (only if nothing found yet)
       if (!GENERIC_FAMILIES.has(family.toLowerCase()) && !resolvedFamily) {
           resolvedFamily = family;
       }
   }

   return { resolvedFamily, fontIds };
}

/**
* Normalises a font-family string for safe use in inline CSS:
* wraps multi-word names in single quotes.
*/
export function normalizeFontFamily(ff) {
   if (!ff) return '';
   return ff
       .split(',')
       .map((f) => {
           const t = f.replace(/^["']+|["']+$/g, '').trim();
           return t.includes(' ') ? `'${t}'` : t;
       })
       .join(', ');
}